import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CRYPTO_LIST } from '../data/constants';
import { apiRequest, getStoredToken } from '../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../utils/idempotency';
import { readCachedValue, readStorageJSON, writeCachedValue } from '../utils/storage';
import { useAuth } from './AuthContext';

const WalletsContext = createContext(null);
const PUBLIC_WALLETS_CACHE_KEY = 'payrocky_wallets_public';
const ADMIN_WALLETS_CACHE_KEY = 'payrocky_wallets_admin';
const AUTH_USER_CACHE_KEY = 'payrocky_auth_user';
const WALLETS_CACHE_TTL_MS = 60 * 1000;
const WALLETS_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

function isAuthRoute() {
  if (typeof window === 'undefined') return false;
  const pathname = String(window.location.pathname || '').toLowerCase();
  return pathname === '/login'
    || pathname === '/signup'
    || pathname === '/forgot-password'
    || pathname.startsWith('/reset-password/');
}

function isStoredAdminSession() {
  const token = getStoredToken();
  if (!token) return false;

  const cachedAuth = readStorageJSON(AUTH_USER_CACHE_KEY, null);
  const role = cachedAuth?.value?.role || cachedAuth?.role || '';
  return String(role).toLowerCase() === 'admin';
}

function getWalletsCacheKey() {
  return isStoredAdminSession() ? ADMIN_WALLETS_CACHE_KEY : PUBLIC_WALLETS_CACHE_KEY;
}

function readInitialWallets() {
  const preferredCacheKey = getWalletsCacheKey();
  const preferredWallets = readCachedValue(preferredCacheKey, WALLETS_CACHE_TTL_MS, null);
  if (preferredWallets) return preferredWallets;

  const fallbackCacheKey = preferredCacheKey === ADMIN_WALLETS_CACHE_KEY
    ? PUBLIC_WALLETS_CACHE_KEY
    : ADMIN_WALLETS_CACHE_KEY;

  return readCachedValue(fallbackCacheKey, WALLETS_CACHE_TTL_MS, {});
}

function readStaleWallets(cacheKey) {
  const cached = readStorageJSON(cacheKey, null);
  if (!cached || typeof cached !== 'object') return null;
  return cached.value && typeof cached.value === 'object' ? cached.value : null;
}

function normalizeWallets(source = {}) {
  const next = {};
  CRYPTO_LIST.forEach((crypto) => {
    next[crypto.id] = {};
    crypto.networks.forEach((network) => {
      const currentValue = source?.[crypto.id]?.[network];
      if (currentValue && typeof currentValue === 'object') {
        next[crypto.id][network] = {
          address: currentValue.address || '',
          isEnabled: currentValue.is_enabled !== false && currentValue.isEnabled !== false,
          disabledReason: currentValue.disabled_reason || currentValue.disabledReason || '',
          updatedAt: currentValue.updated_at || currentValue.updatedAt || '',
        };
      } else {
        next[crypto.id][network] = {
          address: currentValue || '',
          isEnabled: true,
          disabledReason: '',
          updatedAt: '',
        };
      }
    });
  });
  return next;
}

function groupWallets(walletSource = []) {
  if (Array.isArray(walletSource)) {
    return walletSource.reduce((acc, wallet) => {
      if (!wallet?.crypto || !wallet?.network) return acc;
      acc[wallet.crypto] = acc[wallet.crypto] || {};
      acc[wallet.crypto][wallet.network] = {
        address: wallet.address || '',
        is_enabled: wallet.is_enabled !== false,
        disabled_reason: wallet.disabled_reason || '',
        updated_at: wallet.updated_at || '',
      };
      return acc;
    }, {});
  }

  if (walletSource && typeof walletSource === 'object') {
    return walletSource;
  }

  return {};
}

async function loadWalletPayload(preferAdmin = false) {
  const token = getStoredToken();

  if (preferAdmin && token) {
    try {
      return await apiRequest('/admin/wallets', { token, timeoutMs: 10000 });
    } catch (error) {
      const message = String(error?.message || '');
      if (!/(admin access|required|forbidden|unauthorized|no token provided|invalid token)/i.test(message)) {
        throw error;
      }
    }
  }

  return apiRequest('/wallets', { timeoutMs: 10000 });
}

function canActivelyRefreshWallets() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

export function WalletsProvider({ children }) {
  const { token, loading: authLoading } = useAuth();
  const [wallets, setWallets] = useState(() => normalizeWallets(readInitialWallets()));
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletsError, setWalletsError] = useState('');

  const refreshWallets = useCallback(async (force = false) => {
    const cacheKey = getWalletsCacheKey();
    if (!force) {
      const cachedWallets = readCachedValue(cacheKey, WALLETS_CACHE_TTL_MS, null);
      if (cachedWallets) {
        const next = normalizeWallets(cachedWallets);
        setWallets(next);
      }
    }

    setWalletsLoading(true);
    setWalletsError('');
    try {
      const res = await loadWalletPayload(cacheKey === ADMIN_WALLETS_CACHE_KEY);
      const next = normalizeWallets(groupWallets(res.wallets || []));
      setWallets(next);
      writeCachedValue(cacheKey, next);
      return next;
    } catch (error) {
      setWalletsError(error.message || 'Failed to load wallets.');
      throw error;
    } finally {
      setWalletsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthRoute()) return;
    if (authLoading && token) return;
    refreshWallets().catch(() => {
      const cachedWallets = readCachedValue(getWalletsCacheKey(), WALLETS_CACHE_TTL_MS, null);
      if (cachedWallets) {
        setWallets(normalizeWallets(cachedWallets));
        return;
      }
      const staleWallets = readStaleWallets(getWalletsCacheKey()) || readStaleWallets(getWalletsCacheKey() === ADMIN_WALLETS_CACHE_KEY ? PUBLIC_WALLETS_CACHE_KEY : ADMIN_WALLETS_CACHE_KEY);
      if (staleWallets) {
        setWallets(normalizeWallets(staleWallets));
        return;
      }
      setWallets((prev) => normalizeWallets(prev && Object.keys(prev).length ? prev : {}));
    });
  }, [authLoading, refreshWallets, token]);

  useEffect(() => {
    if (isAuthRoute()) return undefined;
    if (authLoading && token) return undefined;

    const refreshIfVisible = () => {
      if (!canActivelyRefreshWallets()) return;
      refreshWallets(true).catch(() => {});
    };

    const intervalId = window.setInterval(refreshIfVisible, WALLETS_REFRESH_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfVisible();
      }
    };
    const handleWindowFocus = () => {
      refreshIfVisible();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authLoading, refreshWallets, token]);

  const getWalletConfig = (cryptoId, network) => wallets?.[cryptoId]?.[network] || {
    address: '',
    isEnabled: true,
    disabledReason: '',
    updatedAt: '',
  };

  const getWalletAddress = (cryptoId, network) => getWalletConfig(cryptoId, network).address || '';

  const adminUpdateWallet = async (cryptoId, network, payload) => {
    setWalletsError('');
    const trimmedAddress = String(payload?.address || '').trim();
    const isEnabled = payload?.isEnabled !== false;
    const disabledReason = String(payload?.disabledReason || '').trim();
    const idempotencyPayload = {
      crypto: cryptoId,
      network,
      address: trimmedAddress,
      is_enabled: isEnabled,
      disabled_reason: disabledReason,
    };
    const idempotencyKey = getIdempotencyKey('admin:update-wallet', idempotencyPayload);
    try {
      await apiRequest('/admin/update-wallet', {
        method: 'POST',
        token: getStoredToken(),
        timeoutMs: 10000,
        idempotencyKey,
        body: {
          crypto: cryptoId,
          network,
          address: trimmedAddress,
          is_enabled: isEnabled,
          disabled_reason: disabledReason,
        },
      });
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:update-wallet', idempotencyPayload);
      }
      throw error;
    }

    try {
      const refreshed = await loadWalletPayload(true);
      const next = normalizeWallets(groupWallets(refreshed.wallets || []));
      setWallets(next);
      writeCachedValue(ADMIN_WALLETS_CACHE_KEY, next);
      writeCachedValue(PUBLIC_WALLETS_CACHE_KEY, next);
      releaseIdempotencyKey('admin:update-wallet', idempotencyPayload);
      return next;
    } catch (error) {
      setWallets((prev) => {
        const next = normalizeWallets({
          ...prev,
          [cryptoId]: {
            ...(prev?.[cryptoId] || {}),
            [network]: {
              address: trimmedAddress,
              is_enabled: isEnabled,
              disabled_reason: disabledReason,
              updated_at: new Date().toISOString(),
            },
          },
        });
        writeCachedValue(ADMIN_WALLETS_CACHE_KEY, next);
        writeCachedValue(PUBLIC_WALLETS_CACHE_KEY, next);
        return next;
      });
      setWalletsError('Wallet saved, but we could not refresh the latest wallet list yet.');
      return null;
    }
  };

  const value = useMemo(() => ({
    wallets,
    walletsLoading,
    walletsError,
    refreshWallets,
    getWalletAddress,
    getWalletConfig,
    adminUpdateWallet,
  }), [wallets, walletsLoading, walletsError, refreshWallets]);

  return <WalletsContext.Provider value={value}>{children}</WalletsContext.Provider>;
}

export function useWallets() {
  const ctx = useContext(WalletsContext);
  if (!ctx) throw new Error('useWallets must be used inside <WalletsProvider>');
  return ctx;
}
